/**
 * useModalResize — drag-to-resize, a maximize toggle, and a persisted size for
 * a modal panel that is centered by its overlay (the same geometry
 * `useModalDrag` assumes). The companion to that hook: pass it the same
 * `panelRef` and spread both styles onto the panel.
 *
 * What it gives you:
 *   • `width`/`height` (px) clamped to [min, maxFraction × viewport], read
 *     synchronously from localStorage on mount so there is no flash from
 *     default → saved size;
 *   • `resizeHandleProps` to spread onto a corner grip — dragging it away from
 *     the panel grows it;
 *   • `isMaximized` / `toggleMaximized` for a header button that fills
 *     `maxFraction` of the viewport and restores the previous size;
 *   • persistence of both the chosen size and the maximized flag to
 *     localStorage under the caller's key.
 *
 * Geometry note: a panel centered by its overlay keeps its center as it grows,
 * so a corner only travels half as far as the size changes. With
 * `centerAnchored` (the default) the hook doubles the pointer delta so the grip
 * tracks the pointer 1:1 and the panel grows symmetrically about its center.
 * Pass `centerAnchored: false` for a top-left-anchored panel.
 *
 * Perf: while a resize is in flight the size is written straight to
 * `panelRef.current.style` — no setState per `pointermove`, so a heavy child
 * (a Google Map, a virtualized grid) is not re-rendered at pointer frequency.
 * React state is committed once, on pointerup/pointercancel.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';

export interface ModalSize {
  w: number;
  h: number;
}

export interface UseModalResizeOptions {
  /** Whether the modal is open — gates the viewport listener. */
  active: boolean;
  /** localStorage key the chosen size + maximized flag are persisted under. */
  storageKey: string;
  /**
   * Size when nothing is stored yet. Either a fixed px number or a fraction
   * (0–1] of the matching viewport dimension. Always re-clamped.
   */
  defaultWidth?: number;
  defaultHeight?: number;
  minWidth?: number;
  minHeight?: number;
  /** Largest fraction of the viewport the panel may occupy — also the maximized size. */
  maxFraction?: number;
  /** Share `useModalDrag`'s ref so in-flight resizes can be painted via the DOM. */
  panelRef?: React.RefObject<HTMLElement | null>;
  /** See the geometry note above. Default true. */
  centerAnchored?: boolean;
}

export interface ModalResizeHandleProps {
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerCancel: (e: React.PointerEvent<HTMLElement>) => void;
}

export interface UseModalResizeResult {
  width: number;
  height: number;
  /** Spread onto the panel alongside useModalDrag's `panelStyle`. */
  panelSizeStyle: React.CSSProperties;
  isMaximized: boolean;
  toggleMaximized: () => void;
  /** Spread onto the resize grip. */
  resizeHandleProps: ModalResizeHandleProps;
}

interface StoredSize {
  size: ModalSize;
  maximized: boolean;
}

const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);

/** `<= 1` → a fraction of the viewport; otherwise a fixed px length. */
const resolveDefault = (value: number, viewport: number) =>
  Math.round(value <= 1 ? viewport * value : value);

const readStored = (storageKey: string): StoredSize | null => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const w = Number(parsed?.w);
    const h = Number(parsed?.h);
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return null;
    return { size: { w, h }, maximized: parsed?.max === true };
  } catch {
    return null;
  }
};

export function useModalResize({
  active,
  storageKey,
  defaultWidth = 0.5,
  defaultHeight = 0.5,
  minWidth = 360,
  minHeight = 280,
  maxFraction = 0.95,
  panelRef,
  centerAnchored = true,
}: UseModalResizeOptions): UseModalResizeResult {
  const stored = useRef<StoredSize | null | undefined>(undefined);
  if (stored.current === undefined) stored.current = readStored(storageKey);

  const [viewport, setViewport] = useState<ModalSize>(() => ({
    w: window.innerWidth,
    h: window.innerHeight,
  }));

  // The size the user chose (never the maximized size), so restoring always has
  // something to go back to.
  const [size, setSize] = useState<ModalSize>(
    () =>
      stored.current?.size ?? {
        w: resolveDefault(defaultWidth, window.innerWidth),
        h: resolveDefault(defaultHeight, window.innerHeight),
      },
  );
  const [isMaximized, setIsMaximized] = useState<boolean>(() => stored.current?.maximized ?? false);

  const bounds = useMemo(
    () => ({
      maxW: Math.max(minWidth, Math.round(viewport.w * maxFraction)),
      maxH: Math.max(minHeight, Math.round(viewport.h * maxFraction)),
    }),
    [viewport, minWidth, minHeight, maxFraction],
  );

  const clampSize = useCallback(
    (s: ModalSize): ModalSize => ({
      w: clamp(Math.round(s.w), minWidth, bounds.maxW),
      h: clamp(Math.round(s.h), minHeight, bounds.maxH),
    }),
    [minWidth, minHeight, bounds],
  );

  const effective = useMemo(
    () => (isMaximized ? { w: bounds.maxW, h: bounds.maxH } : clampSize(size)),
    [isMaximized, bounds, clampSize, size],
  );

  const effectiveRef = useRef(effective);
  effectiveRef.current = effective;
  const sizeRef = useRef(size);
  sizeRef.current = size;

  const persist = useCallback(
    (s: ModalSize, maximized: boolean) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify({ w: s.w, h: s.h, max: maximized }));
      } catch {
        /* ignore quota errors */
      }
    },
    [storageKey],
  );

  // A saved size can outgrow a smaller window, and a maximized panel has to
  // follow the viewport.
  useEffect(() => {
    if (!active) return;
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [active]);

  /** In-flight size, painted to the DOM but not yet committed to state. */
  const liveSizeRef = useRef<ModalSize | null>(null);
  const dragRef = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startW: number;
    startH: number;
  } | null>(null);

  const applySize = useCallback(
    (s: ModalSize) => {
      const panel = panelRef?.current;
      if (panel) {
        panel.style.width = `${s.w}px`;
        panel.style.height = `${s.h}px`;
      }
    },
    [panelRef],
  );

  // A render can land mid-resize for reasons unrelated to resizing; reassert the
  // in-flight size so the live gesture always wins until it commits.
  useLayoutEffect(() => {
    if (liveSizeRef.current) applySize(liveSizeRef.current);
  });

  const commitLiveSize = useCallback(() => {
    const live = liveSizeRef.current;
    liveSizeRef.current = null;
    if (!live) return;
    applySize(live);
    setSize(live);
    setIsMaximized(false);
    persist(live, false);
  }, [applySize, persist]);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    e.stopPropagation();
    const start = effectiveRef.current;
    dragRef.current = {
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startW: start.w,
      startH: start.h,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      // A missed pointerup must not let a buttonless mouse resume the gesture.
      if (e.pointerType === 'mouse' && e.buttons === 0) {
        dragRef.current = null;
        commitLiveSize();
        return;
      }
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      const factor = centerAnchored ? 2 : 1;
      const next = clampSize({
        w: drag.startW + factor * (e.clientX - drag.startClientX),
        h: drag.startH + factor * (e.clientY - drag.startClientY),
      });
      // Hot path: paint via the DOM, not via a render.
      liveSizeRef.current = next;
      applySize(next);
    },
    [applySize, centerAnchored, clampSize, commitLiveSize],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      dragRef.current = null;
      commitLiveSize();
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    },
    [commitLiveSize],
  );

  const toggleMaximized = useCallback(() => {
    const next = !isMaximized;
    setIsMaximized(next);
    persist(sizeRef.current, next);
  }, [isMaximized, persist]);

  const panelSizeStyle = useMemo<React.CSSProperties>(
    () => ({ width: `${effective.w}px`, height: `${effective.h}px` }),
    [effective],
  );

  const resizeHandleProps = useMemo<ModalResizeHandleProps>(
    () => ({ onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp }),
    [onPointerDown, onPointerMove, onPointerUp],
  );

  return {
    width: effective.w,
    height: effective.h,
    panelSizeStyle,
    isMaximized,
    toggleMaximized,
    resizeHandleProps,
  };
}

export default useModalResize;
